// Blog body → article HTML.
//
// Posts are written in the admin as either raw HTML (older posts) or plain
// text pasted from a doc (what actually happens). A plain-text body used to be
// dumped into innerHTML as one unbroken blob — no paragraphs, no headings, no
// anchors for Google. This normalises both into real article markup.
//
// Markdown-lite is deliberate: `##` headings, `-`/`1.` lists, `**bold**`,
// `[text](url)`, blank line = paragraph. Anything already containing block HTML
// is passed straight through.

const BLOCK_HTML = /<(p|h[1-6]|ul|ol|li|blockquote|div|table|section|article|figure)\b/i;

const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

// Inline markers, applied after escaping so user text can never inject markup.
const inline = (s) => escapeHtml(s)
  .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]*)\)/g, '<a href="$2">$1</a>')
  .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  .replace(/(^|[\s(])\*([^*\n]+)\*/g, '$1<em>$2</em>');

const isListLine = (l) => /^\s*([-*•]|\d+[.)])\s+/.test(l);
const listMarker = (l) => (/^\s*\d+[.)]\s+/.test(l) ? 'ol' : 'ul');
const stripMarker = (l) => l.replace(/^\s*([-*•]|\d+[.)])\s+/, '');

// ponytail: heading heuristic for unmarked plain text — a short block with no
// sentence-ending punctuation reads as a section title. Authors who want
// certainty use "## Heading"; this only rescues legacy pasted posts.
const looksLikeHeading = (block) =>
  !block.includes('\n') &&
  block.length <= 60 &&
  !/[.!?،۔:;؟]$/.test(block.trim()) &&
  block.trim().split(/\s+/).length <= 9;

export function renderBlogBody(raw) {
  const src = String(raw || '').trim();
  if (!src) return '';
  if (BLOCK_HTML.test(src)) return src; // already authored as HTML

  const out = [];
  for (const block of src.replace(/\r\n/g, '\n').split(/\n{2,}/)) {
    const trimmed = block.trim();
    if (!trimmed) continue;

    const heading = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      const level = Math.min(Math.max(heading[1].length, 2), 3); // h1 is the post title
      out.push(`<h${level} dir="auto">${inline(heading[2].trim())}</h${level}>`);
      continue;
    }

    const lines = trimmed.split('\n');
    if (lines.every(isListLine)) {
      const tag = listMarker(lines[0]);
      out.push(`<${tag} dir="auto">${lines.map(l => `<li dir="auto">${inline(stripMarker(l))}</li>`).join('')}</${tag}>`);
      continue;
    }

    if (trimmed.startsWith('> ')) {
      out.push(`<blockquote dir="auto">${inline(lines.map(l => l.replace(/^>\s?/, '')).join(' '))}</blockquote>`);
      continue;
    }

    if (looksLikeHeading(trimmed)) {
      out.push(`<h2 dir="auto">${inline(trimmed)}</h2>`);
      continue;
    }

    out.push(`<p dir="auto">${lines.map(inline).join('<br />')}</p>`);
  }
  return out.join('\n');
}

export default renderBlogBody;
